// src/App.js
import { useState, useEffect, useLayoutEffect, useCallback, memo, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import Home from "./components/Home";
import Header from "./components/Header";
import LegacySearchRedirect from "./components/search/LegacySearchRedirect";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import GlobalFeedback from "./components/GlobalFeedback";
import XpEventNotifier from "./components/XpEventNotifier";
import AuthOverlay from "./components/AuthOverlay";

import api from "./api/axiosConfig";
import { GameModalProvider, useGameModal } from "./context/GameModalContext";
import useAnalytics from "./hooks/useAnalytics";
import useIdleLogout from "./hooks/useIdleLogout";
import RequireAdmin from "./routes/RequireAdmin";
import RequireModerator from "./routes/RequireModerator";
import ActivityTracker from "./components/ActivityTracker";
import { applyAppSettingsToDom, normalizeAppSettings, readLocalAppSettings, writeLocalAppSettings } from "./utils/appSettings";
import { clearOnboardingPreferencesPending } from "./utils/onboardingPreferences";

const Dashboard = lazy(() => import("./components/Dashboard"));
const UserDashboard = lazy(() => import("./components/UserDashboard"));
const PublicProfilePage = lazy(() => import("./components/PublicProfilePage"));
const Privacy = lazy(() => import("./components/Privacy"));
const Terms = lazy(() => import("./components/Terms"));
const CookiesPolicy = lazy(() => import("./components/CookiesPolicy"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const MessagesPage = lazy(() => import("./components/MessagesPage"));
const FeedPage = lazy(() => import("./components/FeedPage"));
const QuickGameSessionModal = lazy(() => import("./components/QuickGameSessionModal"));
const PlaytimeHelpButton = lazy(() => import("./components/PlaytimeHelpButton"));
const ModerationPanelPage = lazy(() => import("./pages/ModerationPanelPage"));
const AdminGamesModerationPage = lazy(() => import("./pages/AdminGamesModerationPage"));
const NewsPage = lazy(() => import("./pages/newsPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));

const GameDetailPage = lazy(() => import("./pages/GameDetailPage"));
const GameReviewsPage = lazy(() => import("./pages/GameReviewsPage"));
const ReviewDetailPage = lazy(() => import("./pages/ReviewDetailPage"));
const SessionDetailPage = lazy(() => import("./pages/SessionDetailPage"));
const ListDetailPage = lazy(() => import("./pages/ListDetailPage"));
const GameSeriesPage = lazy(() => import("./pages/GameSeriesPage"));
const GameEditionsPage = lazy(() => import("./pages/GameEditionsPage"));
const PlatformsIndex = lazy(() => import("./pages/PlatformsIndex"));
const PlatformDetail = lazy(() => import("./pages/PlatformDetail"));
const GenresIndex = lazy(() => import("./pages/GenresIndex"));
const GenresDetail = lazy(() => import("./pages/GenresDetail"));
const ExploreList = lazy(() => import("./pages/ExploreList"));
const Welcome = lazy(() => import("./pages/Welcome"));
const NewsHubPage = lazy(() => import("./pages/NewsHubPage"));
const GamingWorldPage = lazy(() => import("./pages/GamingWorldPage"));
const CompanyDetailPage = lazy(() => import("./pages/CompanyDetailPage"));
const CommunitiesPage = lazy(() => import("./pages/CommunitiesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PersonalProgressPage = lazy(() => import("./pages/PersonalProgressPage"));
const SessionInsightsPage = lazy(() => import("./pages/SessionInsightsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      <span className="route-fallback__spinner" aria-hidden="true" />
      <span>Cargando...</span>
    </div>
  );
}

function resetDocumentScroll() {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  const scroller = document.scrollingElement || document.documentElement;
  if (scroller) scroller.scrollTop = 0;
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function ProfileTabRedirect({ tab, keepSearch = false }) {
  const location = useLocation();
  const next = new URLSearchParams();
  if (tab) next.set("tab", tab);

  if (keepSearch) {
    const current = new URLSearchParams(location.search || "");
    current.forEach((value, key) => {
      if (key === "tab") return;
      next.set(key, value);
    });
  }

  const query = next.toString();
  const to = query ? `/user-profile?${query}` : "/user-profile";
  return <Navigate to={to} replace />;
}

function FriendLegacyRedirect() {
  const { friendId } = useParams();
  const target = friendId ? `/user/${encodeURIComponent(String(friendId))}` : "/user-profile";
  return <Navigate to={target} replace />;
}

const AppContent = memo(function AppContent({
  isAuthenticated,
  authLoading,
  profile,
  handleLogout,
  setIsAuthenticated,
  setProfile,
}) {
  const { selectedGame, closeModal } = useGameModal();
  useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;
  const background = state && state.background;
  const [currentSessionGame, setCurrentSessionGame] = useState(null);
  const [quickSessionOpen, setQuickSessionOpen] = useState(false);
  const [quickSessionInitialGame, setQuickSessionInitialGame] = useState(null);

  const handleAuthSuccess = useCallback((user) => {
    if (user?.role) localStorage.setItem("role", user.role);
    if (user?._id || user?.id) localStorage.setItem("userId", user._id || user.id);
    setIsAuthenticated(true);
    setProfile(user);
  }, [setIsAuthenticated, setProfile]);

  const openQuickSession = useCallback(
    (game = null) => {
      setQuickSessionInitialGame(game || currentSessionGame || null);
      setQuickSessionOpen(true);
    },
    [currentSessionGame]
  );

  // Auto-open quick session when URL has ?session=new (e.g. desde pantalla final de onboarding)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("session") !== "new" || !isAuthenticated) return;
    // Limpiar param via navigate (replaceState no actualiza React Router y causa re-disparos)
    params.delete("session");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
    openQuickSession();
  }, [isAuthenticated, openQuickSession, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    if (background) return;
    let cancelled = false;
    let rafId = 0;
    const timers = [];
    const cancel = () => {
      cancelled = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timerId) => window.clearTimeout(timerId));
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchmove", cancel);
      window.removeEventListener("keydown", cancel);
    };
    const reset = () => {
      if (!cancelled) resetDocumentScroll();
    };

    reset();
    rafId = window.requestAnimationFrame(() => {
      reset();
      rafId = window.requestAnimationFrame(reset);
    });

    const isGameRoute = location.pathname.startsWith("/game/");
    const delays = isGameRoute ? [80, 220, 520, 1000] : [80, 220];
    delays.forEach((delay) => timers.push(window.setTimeout(reset, delay)));

    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchmove", cancel, { passive: true });
    window.addEventListener("keydown", cancel);

    return cancel;
  }, [background, location.pathname]);

  useEffect(() => {
    if (!selectedGame?.id) return;
    const rawSlug = selectedGame.slug || selectedGame.name || selectedGame.title || "";
    const slug = String(rawSlug)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const to = slug ? `/game/${selectedGame.id}/${slug}` : `/game/${selectedGame.id}`;
    navigate(to, { state: { gamePrefill: selectedGame } });
    closeModal();
  }, [selectedGame, closeModal, navigate]);

  // ✅ Cierre automático por inactividad (solo si está logueado)
  useIdleLogout(
    isAuthenticated
      ? () => {
          handleLogout();
          window.location.href = "/";
        }
      : null,
    { limitMs: 60 * 60 * 1000 } // 1 hora
  );

  const protectedRoute = (element, loginTo = "/login") => {
    if (authLoading) return <RouteFallback />;
    return isAuthenticated ? element : <Navigate to={loginTo} />;
  };

  return (
    <>
      <Header
        profile={profile}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        onOpenQuickSession={() => openQuickSession()}
        quickSessionOpen={quickSessionOpen}
      />

      <Suspense fallback={null}>
        <QuickGameSessionModal
          open={quickSessionOpen}
          initialGame={quickSessionInitialGame}
          onClose={() => setQuickSessionOpen(false)}
        />
      </Suspense>

      <ActivityTracker userId={profile?._id || profile?.id || null} />

      <div className="xh-site-content">
      <Suspense fallback={<RouteFallback />}>
        <Routes location={background || location}>
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Home />} />
          <Route path="/explore" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/game/:id"
            element={
              <GameDetailPage
                isAuthenticated={isAuthenticated}
                profile={profile}
                onSessionGameChange={setCurrentSessionGame}
                onOpenQuickSession={openQuickSession}
              />
            }
          />
          <Route
            path="/game/:id/:slug"
            element={
              <GameDetailPage
                isAuthenticated={isAuthenticated}
                profile={profile}
                onSessionGameChange={setCurrentSessionGame}
                onOpenQuickSession={openQuickSession}
              />
            }
          />
          <Route path="/game/:id/series" element={<GameSeriesPage />} />
          <Route path="/game/:id/:slug/series" element={<GameSeriesPage />} />
          <Route path="/game/:id/editions" element={<GameEditionsPage />} />
          <Route path="/game/:id/:slug/editions" element={<GameEditionsPage />} />
          <Route path="/game/:id/reviews" element={<GameReviewsPage />} />
          <Route path="/game/:id/:slug/reviews" element={<GameReviewsPage />} />
          <Route path="/company/:id" element={<CompanyDetailPage />} />
          <Route path="/company/:id/:slug" element={<CompanyDetailPage />} />
          <Route path="/communities" element={<CommunitiesPage />} />
          <Route path="/communities/create" element={<CommunitiesPage />} />
          <Route path="/community/:communityRef" element={<CommunitiesPage />} />
          <Route path="/review/:reviewId" element={<ReviewDetailPage />} />
          <Route path="/review/:reviewId/:slug" element={<ReviewDetailPage />} />
          <Route path="/sessions/:sessionId" element={<SessionDetailPage />} />
          <Route path="/list/:listId" element={<ListDetailPage isAuthenticated={isAuthenticated} />} />
          <Route path="/list/:listId/:slug" element={<ListDetailPage isAuthenticated={isAuthenticated} />} />
          <Route path="/browse/:collection" element={<ExploreList />} />
          <Route path="/search" element={<SearchPage profile={profile} />} />
          <Route path="/results" element={<LegacySearchRedirect />} />
          <Route path="/platforms" element={<PlatformsIndex />} />
          <Route path="/platforms/:slug" element={<PlatformDetail />} />
          <Route path="/genres" element={<GenresIndex />} />
          <Route path="/genres/:slug" element={<GenresDetail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/welcome" element={<Welcome isAuthenticated={isAuthenticated} />} />
          <Route path="/pricing" element={<PricingPage isAuthenticated={isAuthenticated} />} />
          <Route path="/news" element={<NewsHubPage />} />
          <Route path="/mundo-gaming" element={<GamingWorldPage />} />
          <Route path="/news/:appid/:gid" element={<NewsPage />} />

          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <>
                  <Home />
                  <AuthOverlay onAuthSuccess={handleAuthSuccess} />
                </>
              )
            }
          />

          <Route
            path="/dashboard"
            element={
              authLoading ? (
                <RouteFallback />
              ) : (
                <Dashboard isAuthenticated={isAuthenticated} />
              )
            }
          />
          <Route
            path="/onboarding/preferences"
            element={protectedRoute(<Navigate to="/welcome" replace />, "/login?mode=register")}
          />
          <Route path="/mi-xp" element={protectedRoute(<PersonalProgressPage profile={profile} />)} />
          <Route path="/mi-xp/estadisticas" element={protectedRoute(<SessionInsightsPage />)} />
          <Route path="/mi-xphub" element={<Navigate to="/mi-xp" replace />} />
          <Route path="/feed" element={protectedRoute(<FeedPage />)} />
          <Route path="/messages" element={protectedRoute(<MessagesPage />)} />
          <Route path="/moderation" element={<RequireModerator><ModerationPanelPage /></RequireModerator>} />
          <Route path="/moderation/games" element={<RequireAdmin><AdminGamesModerationPage /></RequireAdmin>} />
          <Route path="/admin" element={<RequireModerator><Navigate to="/moderation" replace /></RequireModerator>} />
          <Route path="/user-profile" element={protectedRoute(<UserDashboard profile={profile} />)} />
          <Route path="/user/:userId" element={<PublicProfilePage profile={profile} />} />
          <Route path="/friend/:friendId" element={<FriendLegacyRedirect />} />
          <Route path="/settings" element={protectedRoute(<SettingsPage onProfileUpdate={setProfile} />)} />
          <Route path="/reviews" element={protectedRoute(<ProfileTabRedirect tab="reviews" />)} />
          <Route path="/games" element={protectedRoute(<ProfileTabRedirect tab="games" keepSearch />)} />
          <Route path="/lists" element={protectedRoute(<ProfileTabRedirect tab="lists" />)} />
          <Route path="/friends" element={protectedRoute(<ProfileTabRedirect tab="" />)} />
          <Route path="/likes" element={protectedRoute(<ProfileTabRedirect tab="" />)} />
          <Route path="/activity" element={<Navigate to="/user-profile" replace />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {background && (
          <Routes>
            <Route path="/news/:appid/:gid" element={<NewsPage modal />} />
            {!isAuthenticated ? (
              <Route path="/login" element={<AuthOverlay onAuthSuccess={handleAuthSuccess} />} />
            ) : null}
          </Routes>
        )}
      </Suspense>
      </div>

      <Suspense fallback={null}>
        {null /* beta v0.1: chat oculto — mensajería sin real-time */}
        {isAuthenticated && !location.pathname.startsWith("/search") ? <PlaytimeHelpButton userKey={profile?._id || profile?.id || "me"} /> : null}
      </Suspense>
      <Footer />
    </>
  );
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const localSettings = readLocalAppSettings();
    applyAppSettingsToDom(localSettings);
  }, []);

  const handleLogout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    localStorage.removeItem("isAdmin");
    clearOnboardingPreferencesPending();
    sessionStorage.clear();
    setIsAuthenticated(false);
    setProfile(null);
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get("/auth/me", { skipGlobalErrorToast: true });
      const user = res.data.user;
      setProfile(user);
      setIsAuthenticated(true);
      if (user?.onboarding?.completedAt || user?.onboarding?.skippedAt) {
        clearOnboardingPreferencesPending();
      }
      if (user?.role) localStorage.setItem("role", user.role);
      if (user?._id || user?.id) localStorage.setItem("userId", user._id || user.id);

      setLoading(false);

      api.get("/user/app-settings", { skipGlobalErrorToast: true })
        .then((settingsRes) => {
          const payload =
            settingsRes?.data?.data?.appSettings ||
            settingsRes?.data?.appSettings ||
            settingsRes?.data?.data ||
            null;
          if (payload) {
            const normalized = normalizeAppSettings(payload);
            writeLocalAppSettings(normalized);
            applyAppSettingsToDom(normalized);
          }
        })
        .catch(() => {
          // Fallback local ya aplicado al iniciar app.
        });
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        console.error("Error cargando perfil:", error);
      }
      setIsAuthenticated(false);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <GameModalProvider>
      <Router>
        <AppContent
          key={profile?._id || "no-user"}
          isAuthenticated={isAuthenticated}
          authLoading={loading}
          profile={profile}
          handleLogout={handleLogout}
          setIsAuthenticated={setIsAuthenticated}
          setProfile={setProfile}
        />
        <GlobalFeedback />
        {isAuthenticated ? <XpEventNotifier profile={profile} /> : null}
        <CookieBanner />
      </Router>
    </GameModalProvider>
  );
}

export default App;
